import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, AlertTriangle, ArrowRight } from 'lucide-react'
import { getPoolIcon, SystemIcon, AccountIcon } from '@/lib/entity-icons'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useAllPoolSystems } from '@/hooks/use-systems'
import { useAccounts } from '@/hooks/use-accounts'
import { useIncognito } from '@/hooks/use-incognito'
import type { components } from '../../../../../types/flex-api'

type Pool = components['schemas']['Pool']

interface SystemsAnalyticsProps {
  pools: Pool[]
  poolsLoading: boolean
}

// ── Section 1: Top Pools by System Count ──────────────────────────────────────

function TopPoolsCard({ pools, loading }: { pools: Pool[]; loading: boolean }) {
  const incognito = useIncognito()
  const topPools = useMemo(
    () => [...pools].sort((a, b) => b.systemCount - a.systemCount).slice(0, 10),
    [pools]
  )
  const uniqueAccountIds = useMemo(() => [...new Set(topPools.map((p) => p.accountID))], [topPools])
  const accounts = useAccounts(uniqueAccountIds)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Top Pools by System Count</CardTitle>
        <CardDescription>Largest pools ranked by number of systems</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10 pl-6">#</TableHead>
              <TableHead>Pool</TableHead>
              <TableHead>Account</TableHead>
              <TableHead className="text-right pr-6">Systems</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell className="pl-6"><div className="h-4 w-4 bg-muted animate-pulse rounded" /></TableCell>
                    <TableCell><div className="h-4 w-32 bg-muted animate-pulse rounded" /></TableCell>
                    <TableCell><div className="h-4 w-24 bg-muted animate-pulse rounded" /></TableCell>
                    <TableCell className="pr-6"><div className="h-4 w-8 bg-muted animate-pulse rounded ml-auto" /></TableCell>
                  </TableRow>
                ))
              : topPools.map((pool, i) => (
                  <TableRow key={pool.poolID}>
                    <TableCell className="pl-6 text-muted-foreground">{i + 1}</TableCell>
                    <TableCell>
                      {(() => {
                        const PoolIcon = getPoolIcon(pool)
                        return (
                          <Link
                            to={`/pools/${pool.poolID}`}
                            className="flex items-center gap-1.5 text-primary hover:underline group"
                          >
                            <PoolIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                            <span className="truncate max-w-[160px]">{incognito.poolName(pool.name)}</span>
                            <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                          </Link>
                        )
                      })()}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {!incognito.isIncognito && accounts[pool.accountID]?.customization?.logo
                          ? <img src={accounts[pool.accountID]!.customization!.logo} alt="" className="h-4 w-4 object-contain flex-shrink-0" />
                          : <AccountIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        }
                        <span className="text-sm">{incognito.accountName(accounts[pool.accountID]?.name ?? pool.accountID)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right pr-6">
                      <Badge variant="secondary">{pool.systemCount}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

// ── Section 2: By Account ─────────────────────────────────────────────────────

function ByAccountCard({ pools, loading }: { pools: Pool[]; loading: boolean }) {
  const incognito = useIncognito()
  const uniqueAccountIds = useMemo(() => [...new Set(pools.map((p) => p.accountID))], [pools])
  const accounts = useAccounts(uniqueAccountIds)
  const rows = useMemo(() => {
    const map: Record<string, { poolCount: number; totalSystems: number }> = {}
    pools.forEach((p) => {
      if (!map[p.accountID]) map[p.accountID] = { poolCount: 0, totalSystems: 0 }
      map[p.accountID].poolCount += 1
      map[p.accountID].totalSystems += p.systemCount
    })
    return Object.entries(map)
      .sort((a, b) => b[1].totalSystems - a[1].totalSystems)
      .slice(0, 10)
  }, [pools])

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">By Account</CardTitle>
        <CardDescription>Pool and system counts grouped by account</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-6">Account</TableHead>
              <TableHead className="text-right">Pools</TableHead>
              <TableHead className="text-right pr-6">Systems</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell className="pl-6"><div className="h-4 w-32 bg-muted animate-pulse rounded" /></TableCell>
                    <TableCell><div className="h-4 w-8 bg-muted animate-pulse rounded ml-auto" /></TableCell>
                    <TableCell className="pr-6"><div className="h-4 w-10 bg-muted animate-pulse rounded ml-auto" /></TableCell>
                  </TableRow>
                ))
              : rows.map(([accountID, { poolCount, totalSystems }]) => (
                  <TableRow key={accountID}>
                    <TableCell className="pl-6">
                      <Link
                        to={`/accounts/${accountID}`}
                        className="flex items-center gap-2 text-primary hover:underline group"
                      >
                        {!incognito.isIncognito && accounts[accountID]?.customization?.logo
                          ? <img src={accounts[accountID]!.customization!.logo} alt="" className="h-4 w-4 object-contain flex-shrink-0" />
                          : <AccountIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        }
                        <span>{incognito.accountName(accounts[accountID]?.name ?? accountID)}</span>
                        <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </Link>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline">{poolCount}</Badge>
                    </TableCell>
                    <TableCell className="text-right pr-6">
                      <Badge variant="secondary">{totalSystems}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

// ── Section 3: Multi-Pool Members ─────────────────────────────────────────────

function MultiPoolMembersCard({
  members,
  pools,
  loading,
  errorCount,
}: {
  members: { systemID: string; poolIDs: string[]; poolCount: number }[]
  pools: Pool[]
  loading: boolean
  errorCount: number
}) {
  const incognito = useIncognito()
  const poolNameMap = useMemo(() => {
    const m: Record<string, string> = {}
    pools.forEach((p) => { m[p.poolID] = p.name })
    return m
  }, [pools])

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Multi-Pool Members</CardTitle>
            <CardDescription>
              Systems belonging to 2 or more pools
              {!loading && members.length > 0 && (
                <span className="ml-1">— {members.length} found</span>
              )}
            </CardDescription>
          </div>
          {errorCount > 0 && (
            <Badge variant="outline" className="text-amber-600 border-amber-400 gap-1">
              <AlertTriangle className="h-3 w-3" />
              {errorCount} pool{errorCount !== 1 ? 's' : ''} failed
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span className="text-sm">Loading system memberships…</span>
          </div>
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <SystemIcon className="h-10 w-10 mb-3 opacity-40" />
            <p className="text-sm">No systems found in multiple pools</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>System ID</TableHead>
                <TableHead className="text-center w-24">Pool Count</TableHead>
                <TableHead>Pools</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map(({ systemID, poolIDs, poolCount }) => (
                <TableRow key={systemID}>
                  <TableCell
                    className="font-mono text-xs cursor-pointer hover:text-primary transition-colors"
                    title={incognito.isIncognito ? undefined : systemID}
                  >
                    <Link to={`/systems/${encodeURIComponent(systemID)}`} className="hover:underline">
                      {incognito.uuid(systemID)}
                    </Link>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge>{poolCount}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    <span
                      className="truncate block max-w-[400px]"
                      title={poolIDs.map((id) => poolNameMap[id] ?? id).join(', ')}
                    >
                      {poolIDs
                        .map((id) => incognito.poolName(poolNameMap[id] ?? id))
                        .join(', ')}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function SystemsAnalytics({ pools, poolsLoading }: SystemsAnalyticsProps) {
  const { idsLoading, idsErrors, multiPoolMembers } = useAllPoolSystems(pools)

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <TopPoolsCard pools={pools} loading={poolsLoading} />
        <ByAccountCard pools={pools} loading={poolsLoading} />
      </div>
      <MultiPoolMembersCard
        members={multiPoolMembers}
        pools={pools}
        loading={idsLoading}
        errorCount={idsErrors}
      />
    </div>
  )
}
